// Components/Pages/ThematicPillars.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const ThematicPillars = () => {
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPillars();
  }, []);

  const fetchPillars = async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/pillars');
      if (!response.ok) {
        throw new Error('Failed to fetch pillars');
      }
      const data = await response.json();
      setPillars(data);
    } catch (error) {
      console.error('Error fetching pillars:', error);
      setError('Failed to load thematic pillars. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Get tag color based on tag name (fallback)
  const getTagColor = (tag) => {
    const tagColors = {
      'Gender Equality': 'bg-pink-100 text-pink-700',
      'Health & Nutrition': 'bg-green-100 text-green-700',
      'Economic Empowerment': 'bg-blue-100 text-blue-700',
      'Climate Justice': 'bg-emerald-100 text-emerald-700',
    };
    return tagColors[tag] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading thematic pillars...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 max-w-md mx-auto">
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={fetchPillars}
            className="mt-4 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primaryBlue mb-4">
            Thematic Pillars
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Our work is organized around four interconnected thematic pillars that guide our programs and projects in creating sustainable community transformation in Western Kenya.
          </p>
          <div className="w-24 h-1 bg-primaryGold mx-auto mt-4"></div>
        </div>

        {/* Pillars List - Numbered Style */}
        {pillars.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No thematic pillars available yet.</p>
            <p className="text-gray-400 text-sm mt-2">Check back soon for updates.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pillars.map((pillar, index) => (
              <div 
                key={pillar.id}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              >
                <div className="p-6 md:p-8">
                  {/* Number and Title */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primaryGold text-primaryBlue font-bold text-sm">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h2 className="text-xl md:text-2xl font-bold text-primaryBlue">
                          {pillar.title}
                        </h2>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${pillar.tag_color || getTagColor(pillar.tag)}`}>
                          {pillar.tag}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-gray-700 leading-relaxed">
                        {pillar.description}
                      </p>

                      {/* Associated Projects */}
                      {pillar.projects && pillar.projects.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-gray-600">Projects:</span>
                          {pillar.projects.map((project, idx) => (
                            <Link
                              key={idx}
                              to="/projects"
                              className={`inline-block px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 
                                ${pillar.tag_color || getTagColor(pillar.tag)} hover:opacity-80 hover:scale-105`}
                            >
                              {project}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <Link
            to="/projects"
            className="inline-block bg-primaryGold text-primaryBlue px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Explore Our Projects
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ThematicPillars;